import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Grid3x3, Info, Building2 } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import {
  api,
  type MatrixDimension,
  type VendorMatrixResponse,
} from '@/lib/api';

export function MatrixPage() {
  const { getToken } = useAuth();
  const [dimension, setDimension] = useState<MatrixDimension>('aircraft');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['vendor-matrix', dimension],
    queryFn: () =>
      api<VendorMatrixResponse>(`/api/vendors/matrix?dimension=${dimension}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 30_000,
  });

  return (
    <AppShell
      title="Vendor matrix"
      subtitle={
        dimension === 'aircraft'
          ? 'Who quotes what aircraft platforms — count, win rate, lead time'
          : 'Vendors by part category (specialization-tagged)'
      }
      headerActions={
        <div className="inline-flex rounded-md border border-border bg-surface p-0.5 shadow-sm">
          <button
            onClick={() => setDimension('aircraft')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
              dimension === 'aircraft'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            Aircraft
          </button>
          <button
            onClick={() => setDimension('part_category')}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
              dimension === 'part_category'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            Part category
          </button>
        </div>
      }
    >
      <div className="h-full overflow-auto px-6 py-6">
        {isLoading && (
          <div className="card p-8 text-sm text-foreground-muted text-center">Loading matrix…</div>
        )}
        {isError && (
          <div className="card p-8 text-sm text-danger text-center">
            {(error as { message?: string })?.message ?? 'Failed to load matrix'}
          </div>
        )}
        {data && data.rows.length === 0 && (
          <EmptyState dimension={dimension} />
        )}
        {data && data.rows.length > 0 && <MatrixTable data={data} />}

        {data && data.rows.length > 0 && <MatrixLegend />}
      </div>
    </AppShell>
  );
}

function EmptyState({ dimension }: { dimension: MatrixDimension }) {
  return (
    <div className="card p-12 text-center">
      <div className="w-12 h-12 mx-auto rounded-full bg-primary-muted text-primary-strong flex items-center justify-center mb-4">
        <Grid3x3 className="w-6 h-6" />
      </div>
      <h3 className="font-semibold mb-1">No matrix data yet</h3>
      <p className="text-sm text-foreground-muted max-w-md mx-auto">
        {dimension === 'aircraft'
          ? 'Vendors appear here once they\'ve quoted on RFQs with aircraft_platform set.'
          : 'Vendors appear here once they have part_category specialization tags. Add tags from a vendor\'s scorecard.'}
      </p>
      <Link href="/vendors" className="btn-outline mt-5 mx-auto inline-flex">
        <Building2 className="w-4 h-4" /> Go to vendors
      </Link>
    </div>
  );
}

function MatrixTable({ data }: { data: VendorMatrixResponse }) {
  return (
    <div className="card overflow-hidden">
      <div className="overflow-auto max-h-[calc(100dvh-260px)]">
        <table className="min-w-full text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="sticky left-0 z-20 bg-surface-muted border-b border-r border-border px-4 py-3 text-left text-[10px] uppercase tracking-[0.08em] font-semibold text-foreground-muted">
                Vendor
              </th>
              {data.cols.map((c) => (
                <th
                  key={c}
                  className="bg-surface-muted border-b border-border px-3 py-3 text-center text-[10px] uppercase tracking-[0.08em] font-semibold text-foreground-muted whitespace-nowrap min-w-[110px]"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, ri) => (
              <tr key={row.id} className={ri % 2 === 0 ? 'bg-surface' : 'bg-surface-muted/30'}>
                <td
                  className={`sticky left-0 z-10 ${
                    ri % 2 === 0 ? 'bg-surface' : 'bg-surface-muted/95'
                  } border-b border-r border-border px-4 py-2.5 font-medium text-sm whitespace-nowrap`}
                >
                  <Link
                    href={`/vendors/${row.id}`}
                    className="text-foreground hover:text-primary transition-colors"
                  >
                    {row.name}
                  </Link>
                </td>
                {data.cols.map((c) => {
                  const cell = data.cells[`${row.id}::${c}`];
                  return (
                    <td
                      key={c}
                      className="border-b border-border px-3 py-2.5 text-center align-middle"
                    >
                      <MatrixCell cell={cell} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MatrixCell({
  cell,
}: {
  cell: { quoteCount: number; winCount: number; winRate: number | null; avgLeadTimeDays: number | null } | undefined;
}) {
  if (!cell || cell.quoteCount === 0) {
    return <span className="text-foreground-muted/40">—</span>;
  }
  // Heatmap: color intensity by quote count (capped at 10 for normalization).
  // Win rate above 50% nudges toward green; below toward neutral.
  const heat = Math.min(cell.quoteCount, 10) / 10;
  const winColor =
    cell.winRate != null && cell.winRate >= 50
      ? 'success'
      : cell.winRate != null && cell.winRate >= 25
        ? 'primary'
        : 'default';

  // Map heat 0-1 to opacity classes (rough quantization).
  const bgOpacity =
    heat >= 0.7 ? 'opacity-100' : heat >= 0.4 ? 'opacity-80' : heat >= 0.2 ? 'opacity-60' : 'opacity-40';
  const bgClass =
    winColor === 'success'
      ? `bg-success-muted ${bgOpacity}`
      : winColor === 'primary'
        ? `bg-primary-muted ${bgOpacity}`
        : `bg-muted ${bgOpacity}`;

  return (
    <div
      className={`inline-flex flex-col items-center justify-center min-w-[88px] px-2 py-1.5 rounded ${bgClass}`}
      title={`Quotes: ${cell.quoteCount}\nWins: ${cell.winCount}\nWin rate: ${cell.winRate != null ? cell.winRate.toFixed(1) + '%' : '—'}\nAvg lead time: ${cell.avgLeadTimeDays != null ? cell.avgLeadTimeDays.toFixed(1) + 'd' : '—'}`}
    >
      <div className="text-sm font-bold tabular-nums">{cell.quoteCount}</div>
      <div className="flex items-center gap-1 mt-0.5 text-[10px] text-foreground-muted">
        {cell.winRate != null && (
          <span
            className={`font-medium ${
              winColor === 'success'
                ? 'text-success'
                : winColor === 'primary'
                  ? 'text-primary-strong'
                  : ''
            }`}
          >
            {cell.winRate.toFixed(0)}% win
          </span>
        )}
        {cell.avgLeadTimeDays != null && (
          <>
            <span>·</span>
            <span>{cell.avgLeadTimeDays.toFixed(0)}d</span>
          </>
        )}
      </div>
    </div>
  );
}

function MatrixLegend() {
  return (
    <div className="mt-4 card p-3 px-4 flex items-center gap-4 flex-wrap text-xs text-foreground-muted">
      <div className="flex items-center gap-1.5">
        <Info className="w-3.5 h-3.5" />
        <span className="font-medium text-foreground">How to read this:</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-4 h-4 rounded bg-muted opacity-40" />
        <span>1-2 quotes</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-4 h-4 rounded bg-primary-muted opacity-100" />
        <span>≥3 quotes, 25-49% win</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-4 h-4 rounded bg-success-muted opacity-100" />
        <span>≥3 quotes, 50%+ win</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span>Cell shows: <span className="font-mono">quotes · win% · avg lead time</span></span>
      </div>
    </div>
  );
}
